// Add / edit site drawer — a right `Drawer` (a bottom sheet on phones, the
// shared primitive does that) holding the site form built from forms.tsx:
// Name, State (Indian states / UTs), Latitude / Longitude (optional), Power MW,
// Energy MWh, SoC window, Round-trip efficiency %, C-rate (optional). Rules
// mirror the BFF's `_validate_site` so a rejected payload is caught before the
// round-trip; the server message still surfaces in an ErrorPanel if it isn't.
//
// Mounted once by the provider (SiteOverlays); opened through
// `useSite().openAddSite()` / `openEditSite(id)` from the header switcher, the
// launcher, /asset/sites and the site detail page. One lime action per view:
// "Create site" / "Save changes" in the footer; Cancel is a ghost.

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "urql";
import { toast } from "sonner";
import { Save } from "lucide-react";

import { ActionButton, Drawer, ErrorPanel } from "@/app/console/ui";
import { Field, Input, Select } from "@/app/console/forms";
import { CreateAssetDocument, UpdateAssetDocument, type CreateAssetInput, type SiteAsset, type UpdateAssetInput } from "@/app/queriesSites";
import { STATE_OPTIONS } from "@/app/site/indianStates";
import { useSite } from "@/app/site/SiteContext";

interface FormValues {
  name: string;
  state: string;
  lat: string;
  lon: string;
  mw: string;
  mwh: string;
  socMin: string;
  socMax: string;
  rte: string;
  cRate: string;
}
type FieldKey = keyof FormValues;
type Errors = Partial<Record<FieldKey, string>>;

const EMPTY: FormValues = { name: "", state: "", lat: "", lon: "", mw: "", mwh: "", socMin: "20", socMax: "80", rte: "", cRate: "" };

function fromSite(s: SiteAsset): FormValues {
  const str = (v: number | null | undefined) => (v == null ? "" : String(v));
  return {
    name: s.name,
    state: s.locationState ?? "",
    lat: str(s.latitude),
    lon: str(s.longitude),
    mw: str(s.powerRatingMw),
    mwh: str(s.capacityMwh),
    socMin: str(s.socMinPct),
    socMax: str(s.socMaxPct),
    rte: str(s.roundTripEfficiencyPct),
    cRate: str(s.cRate),
  };
}

const num = (v: string): number | null => (v.trim() === "" ? null : Number(v));

/** Same rules as graphql-bff `_validate_site`. */
export function validateSite(f: FormValues): Errors {
  const e: Errors = {};
  const name = f.name.trim();
  if (name.length < 2 || name.length > 120) e.name = "2–120 characters.";
  if (!f.state) e.state = "Choose a state or union territory.";
  const mw = num(f.mw);
  if (mw === null || !Number.isFinite(mw) || mw <= 0) e.mw = "Must be greater than 0.";
  const mwh = num(f.mwh);
  if (mwh === null || !Number.isFinite(mwh) || mwh <= 0) e.mwh = "Must be greater than 0.";
  const lat = num(f.lat);
  if (lat !== null && (!Number.isFinite(lat) || lat < -90 || lat > 90)) e.lat = "−90 to 90.";
  const lon = num(f.lon);
  if (lon !== null && (!Number.isFinite(lon) || lon < -180 || lon > 180)) e.lon = "−180 to 180.";
  const socMin = num(f.socMin);
  const socMax = num(f.socMax);
  if (socMin === null || !Number.isFinite(socMin) || socMin < 0 || socMin > 100) e.socMin = "0 to 100.";
  if (socMax === null || !Number.isFinite(socMax) || socMax < 0 || socMax > 100) e.socMax = "0 to 100.";
  if (!e.socMin && !e.socMax && socMin !== null && socMax !== null && socMin >= socMax) e.socMax = "Max must be above min.";
  const rte = num(f.rte);
  if (rte !== null && (!Number.isFinite(rte) || rte <= 0 || rte > 100)) e.rte = "Above 0, up to 100.";
  const cr = num(f.cRate);
  if (cr !== null && (!Number.isFinite(cr) || cr <= 0)) e.cRate = "Must be greater than 0.";
  return e;
}

export function SiteDrawer() {
  const { drawer, closeDrawer, sites, setCurrentSite, refetchSites } = useSite();
  const navigate = useNavigate();
  const open = drawer.kind !== "closed";
  const editing = useMemo(() => (drawer.kind === "edit" ? sites.find((s) => s.id === drawer.id) ?? null : null), [drawer, sites]);
  const isEdit = drawer.kind === "edit";

  const [form, setForm] = useState<FormValues>(EMPTY);
  const [touched, setTouched] = useState<Partial<Record<FieldKey, true>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const [createRes, runCreate] = useMutation(CreateAssetDocument);
  const [updateRes, runUpdate] = useMutation(UpdateAssetDocument);
  const saving = createRes.fetching || updateRes.fetching;

  // (Re)seed on every open — from the site when editing, blank when creating.
  useEffect(() => {
    if (!open) return;
    setForm(editing ? fromSite(editing) : EMPTY);
    setTouched({});
    setSubmitted(false);
    setServerError(null);
  }, [open, editing]);

  const errors = useMemo(() => validateSite(form), [form]);
  const valid = Object.keys(errors).length === 0;
  const show = (k: FieldKey) => (touched[k] || submitted ? errors[k] : undefined);
  const set = (k: FieldKey) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const blur = (k: FieldKey) => () => setTouched((t) => ({ ...t, [k]: true }));

  // Suggested C-rate = MW ÷ MWh, shown as the placeholder.
  const cRateHint = useMemo(() => {
    const mw = num(form.mw);
    const mwh = num(form.mwh);
    return mw && mwh && mw > 0 && mwh > 0 ? (Math.round((mw / mwh) * 100) / 100).toString() : "0.25";
  }, [form.mw, form.mwh]);

  async function submit() {
    setSubmitted(true);
    setServerError(null);
    if (!valid) return;
    const name = form.name.trim();
    if (isEdit && editing) {
      const input: UpdateAssetInput = {
        name,
        locationState: form.state,
        latitude: num(form.lat),
        longitude: num(form.lon),
        powerRatingMw: Number(form.mw),
        capacityMwh: Number(form.mwh),
        socMinPct: Number(form.socMin),
        socMaxPct: Number(form.socMax),
        roundTripEfficiencyPct: num(form.rte),
        cRate: num(form.cRate),
      };
      const res = await runUpdate({ id: editing.id, input });
      if (res.error || !res.data) {
        setServerError(res.error?.graphQLErrors[0]?.message ?? res.error?.message ?? "Could not save the site.");
        return;
      }
      refetchSites();
      closeDrawer();
      toast.success("Site updated", { description: res.data.updateAsset.name });
      return;
    }
    const input: CreateAssetInput = {
      name,
      locationState: form.state,
      latitude: num(form.lat),
      longitude: num(form.lon),
      powerRatingMw: Number(form.mw),
      capacityMwh: Number(form.mwh),
      socMinPct: Number(form.socMin),
      socMaxPct: Number(form.socMax),
      roundTripEfficiencyPct: num(form.rte),
      cRate: num(form.cRate),
    };
    const res = await runCreate({ input });
    if (res.error || !res.data) {
      setServerError(res.error?.graphQLErrors[0]?.message ?? res.error?.message ?? "Could not create the site.");
      return;
    }
    const created = res.data.createAsset;
    setCurrentSite(created.id);
    refetchSites();
    closeDrawer();
    toast.success("Site created", {
      description: `${created.name} is now the current site.`,
      duration: 10_000,
      action: { label: "Next: enrol a connector →", onClick: () => navigate("/health/connectors?enrol=1") },
    });
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }

  const NUM = "font-mono";

  return (
    <Drawer
      open={open}
      onClose={closeDrawer}
      title={isEdit ? "Edit site" : "Add site"}
      description={isEdit ? "Identity and operating envelope. Connector wiring lives on the site page." : "A battery site for this tenant. You can enrol its connector right after."}
      width="md"
      accent="var(--pb-product-asset)"
      footer={
        <div className="flex items-center justify-end gap-2">
          <ActionButton variant="ghost" onClick={closeDrawer} disabled={saving}>
            Cancel
          </ActionButton>
          <ActionButton
            icon={Save}
            loading={saving}
            disabled={!valid && submitted}
            title={!valid && submitted ? "Fix the highlighted fields" : undefined}
            onClick={() => void submit()}
          >
            {isEdit ? "Save changes" : "Create site"}
          </ActionButton>
        </div>
      }
    >
      <form onSubmit={onSubmit} className="space-y-5" aria-label={isEdit ? "Edit site" : "Add site"} noValidate>
        {serverError && <ErrorPanel title={isEdit ? "Could not save" : "Could not create the site"} message={serverError} />}

        <fieldset className="space-y-3">
          <legend className="pb-micro mb-1 text-text-3">Identity</legend>
          <Field label="Name" htmlFor="site-name" required error={show("name")} hint={show("name") ? undefined : "2–120 characters, e.g. Bhadla BESS 1."}>
            <Input id="site-name" value={form.name} onChange={set("name")} onBlur={blur("name")} invalid={Boolean(show("name"))} placeholder="Bhadla BESS 1" autoFocus maxLength={120} />
          </Field>
          <Field label="State / UT" htmlFor="site-state" required error={show("state")}>
            <Select<string>
              value={form.state || undefined}
              onChange={(v) => {
                setForm((f) => ({ ...f, state: v }));
                setTouched((t) => ({ ...t, state: true }));
              }}
              options={STATE_OPTIONS}
              placeholder="Choose…"
              ariaLabel="State or union territory"
              className="w-full"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude" htmlFor="site-lat" error={show("lat")} hint={show("lat") ? undefined : "Optional"}>
              <Input id="site-lat" type="number" step="any" inputMode="decimal" value={form.lat} onChange={set("lat")} onBlur={blur("lat")} invalid={Boolean(show("lat"))} placeholder="27.5330" className={NUM} />
            </Field>
            <Field label="Longitude" htmlFor="site-lon" error={show("lon")} hint={show("lon") ? undefined : "Optional"}>
              <Input id="site-lon" type="number" step="any" inputMode="decimal" value={form.lon} onChange={set("lon")} onBlur={blur("lon")} invalid={Boolean(show("lon"))} placeholder="71.9150" className={NUM} />
            </Field>
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="pb-micro mb-1 text-text-3">Rating</legend>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Power (MW)" htmlFor="site-mw" required error={show("mw")}>
              <Input id="site-mw" type="number" step="any" min={0} inputMode="decimal" value={form.mw} onChange={set("mw")} onBlur={blur("mw")} invalid={Boolean(show("mw"))} placeholder="100" className={NUM} />
            </Field>
            <Field label="Energy (MWh)" htmlFor="site-mwh" required error={show("mwh")}>
              <Input id="site-mwh" type="number" step="any" min={0} inputMode="decimal" value={form.mwh} onChange={set("mwh")} onBlur={blur("mwh")} invalid={Boolean(show("mwh"))} placeholder="400" className={NUM} />
            </Field>
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="pb-micro mb-1 text-text-3">Operating envelope</legend>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SoC min (%)" htmlFor="site-soc-min" required error={show("socMin")}>
              <Input id="site-soc-min" type="number" step="any" min={0} max={100} inputMode="decimal" value={form.socMin} onChange={set("socMin")} onBlur={blur("socMin")} invalid={Boolean(show("socMin"))} className={NUM} />
            </Field>
            <Field label="SoC max (%)" htmlFor="site-soc-max" required error={show("socMax")}>
              <Input id="site-soc-max" type="number" step="any" min={0} max={100} inputMode="decimal" value={form.socMax} onChange={set("socMax")} onBlur={blur("socMax")} invalid={Boolean(show("socMax"))} className={NUM} />
            </Field>
            <Field label="Round-trip efficiency (%)" htmlFor="site-rte" error={show("rte")} hint={show("rte") ? undefined : "Optional"}>
              <Input id="site-rte" type="number" step="any" min={0} max={100} inputMode="decimal" value={form.rte} onChange={set("rte")} onBlur={blur("rte")} invalid={Boolean(show("rte"))} placeholder="88" className={NUM} />
            </Field>
            <Field label="C-rate" htmlFor="site-crate" error={show("cRate")} hint={show("cRate") ? undefined : "Optional · MW ÷ MWh"}>
              <Input id="site-crate" type="number" step="any" min={0} inputMode="decimal" value={form.cRate} onChange={set("cRate")} onBlur={blur("cRate")} invalid={Boolean(show("cRate"))} placeholder={cRateHint} className={NUM} />
            </Field>
          </div>
        </fieldset>
        {/* Enter submits via the form; the lime action lives in the drawer footer. */}
        <button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true">
          Submit
        </button>
      </form>
    </Drawer>
  );
}
